import { useUpdateUserRoleMutation } from 'api';
import ConfirmationModal from 'common/components/ConfirmationModal';
import { ToastMsg } from 'common/components/Toast';
import { Button, Modal, RadioButton, Typography } from 'components';
import { IcoClose } from 'icons/indexed-icons/IcoClose';
import TriangleWarning from 'icons/indexed-icons/TriangleWarning';
import {
  StyledButtonRow,
  StyledModalHeaderContainer,
  StyledTitle
} from 'pages/MachineManagement/MachineManagementPopup/MachineImageUploaderStyledComponents';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { CSSProperties } from 'styled-components';
import theme from 'themes';
import { ModalSize } from 'types';
import { UserTypes } from 'types/user-management';

const ModalContent = styled.div`
  padding: 0rem 1.5rem 1.5rem;
  .modal-subtext {
    padding-bottom: 1.25rem;
    border-bottom: 1px solid ${theme.colors.tableborder};
  }
  .user-groups {
    max-height: 20rem;
    overflow: auto;
  }
`;
const Row = styled.div<CSSProperties>`
  display: flex;
  gap: ${({ gap }) => gap};
  padding-bottom: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;
const Col = styled.div`
  min-width: 50%;
`;
export const CloseIcon = styled.span`
  cursor: pointer;
`;
export const Footer = styled.div`
  width: 70%;
  margin-left: auto;
  margin-bottom: 1rem;
`;
const ModalContainer = styled.div`
  .modal-bg {
    background: red !important;
  }
`;
interface Props {
  showEditModal: boolean;
  closeModal: () => void;
  data: UserTypes;
}
const UpdateUser = ({ showEditModal, closeModal, data }: Props): JSX.Element => {
  const { t } = useTranslation(['fpns']);
  const [userGroupsData, setUserGroupsData] = useState(data.groups || []);
  const [isFormChange, setIsFormChange] = useState(false);
  const [showConfirmationModal, setConfirmationModal] = useState(false);

  useEffect(() => {
    setUserGroupsData(data.groups || []);
  }, [data]);

  const [updateUserRole] = useUpdateUserRoleMutation();

  const handleUpdateUser = () => {
    const roleParams = userGroupsData.map((group) => ({
      group_id: group?.id,
      user_id: data.id,
      is_admin: group.isAdmin
    }));

    updateUserRole(roleParams).then(() => {
      ToastMsg({
        heading: 'Success!',
        message: 'Role(s) from Group Monitor has been successfully updated.',
        type: 'success',
        position: 'top-right'
      });
      closeModal();
    });
  };

  const handleRoleChange = (event: ChangeEvent<HTMLInputElement>, role: string) => {
    const { name, checked } = event.target;
    setUserGroupsData((prevGroups) => {
      if (!prevGroups) return [];

      const updatedGroups = prevGroups.map((group) =>
        group.id === name ? { ...group, isAdmin: role === 'admin' && checked } : group
      );

      // Check if the data has actually changed
      const isDataChanged = JSON.stringify(data.groups || []) !== JSON.stringify(updatedGroups);
      setIsFormChange(isDataChanged);

      return updatedGroups;
    });
  };

  const handleCloseModal = () => {
    isFormChange ? setConfirmationModal(isFormChange) : closeModal();
  };
  const handleCloseConfirmationModal = () => {
    setConfirmationModal(false);
  };
  return (
    <>
      {showConfirmationModal && isFormChange && (
        <ModalContainer>
          <ConfirmationModal
            zIndex={999}
            isConfirmPrompt
            isVisible={showConfirmationModal}
            type="warning"
            title={t('unsaved_changes_title', { ns: 'common' }) as string}
            headerIcon={<TriangleWarning />}
            onConfirmCallback={closeModal}
            onCancelCallback={handleCloseConfirmationModal}
          >
            <p>{t('unsaved_changes_message', { ns: 'common' })}</p>
          </ConfirmationModal>
        </ModalContainer>
      )}
      <Modal
        title={
          <StyledModalHeaderContainer justifyContent="space-between">
            <StyledTitle>{t('edit_user_role')}</StyledTitle>
            <CloseIcon onClick={handleCloseModal}>{IcoClose()}</CloseIcon>
          </StyledModalHeaderContainer>
        }
        allowContentScroll
        onClose={() => {
          handleCloseModal();
        }}
        showCloseButton={false}
        size={ModalSize.XSMALL_AUTO_HEIGHT}
        widthOverride="50rem"
        visible={showEditModal}
      >
        <ModalContent>
          <Typography className="modal-subtext" color={theme.colors.lightGrey8}>
            {t('change_group_role')}
          </Typography>
          <Row className="user-groups">
            {userGroupsData?.map((group) => {
              return (
                <Col>
                  <Typography weight="semi-bold" mb={'.5rem'}>
                    {group.name}
                  </Typography>
                  <Row gap="1.5rem">
                    <Typography mb={0}>Selected Role:</Typography>
                    <RadioButton
                      id={`adm-${group.id}`}
                      label="Admin"
                      name={group.id}
                      onChange={(e) => handleRoleChange(e, 'admin')}
                      checked={group.isAdmin}
                    />
                    <RadioButton
                      id={`mem-${group.id}`}
                      label="Member"
                      name={group.id}
                      onChange={(e) => handleRoleChange(e, 'member')}
                      checked={!group.isAdmin}
                    />
                  </Row>
                </Col>
              );
            })}
          </Row>

          <Footer>
            <StyledButtonRow>
              <Button
                onClick={handleCloseModal}
                variant="link"
                borderRadius="3rem"
                borderColor={theme.colors.primaryBlue5}
                // color={theme.colors.text.error}
                width="8rem"
              >
                Cancel
              </Button>
              <Button
                disabled={!isFormChange}
                onClick={handleUpdateUser}
                // borderColor={theme.colors.text.error}
                variant="hover-blue"
                bgColor={theme.colors.primaryBlue5}
                borderRadius="3rem"
                color={theme.colors.white}
                width="8rem"
              >
                Update
              </Button>
            </StyledButtonRow>
          </Footer>
        </ModalContent>
      </Modal>
    </>
  );
};

export default UpdateUser;
